import flatMap from 'lodash/flatMap'
import { createSelector } from 'reselect'
import type { PlaylistTag } from 'types/playlist'
import type { Selector, State } from 'types/redux'
import type {
  NativeCategory,
  NativeTag,
  NativeTagWithCategory,
} from 'types/track'
import { sortAlphabetically } from '@disco/utils/sorting'
/**
 * Gets the contact tags from the state.
 * @param state - The Redux state
 * @returns Array of contact tag strings
 */
export const getContactTags = (state: State): string[] =>
  state.entities.tags.contact_tags
/**
 * Gets playlist tags sorted alphabetically.
 * @param state - The Redux state
 * @returns Array of playlist tag objects sorted alphabetically
 */
export const getPlaylistTags: Selector<PlaylistTag[]> = createSelector(
  (state: State) => state.entities.playlistTags,
  (playlistTags: Record<string, PlaylistTag>) => {
    const playlistTagsArray = Object.values(
      playlistTags,
    ) as any as PlaylistTag[]
    return playlistTagsArray.sort(sortAlphabetically)
  },
)
/**
 * Gets playlist tag names as an array of strings.
 * @param state - The Redux state
 * @returns Array of playlist tag names
 */
export const getPlaylistTagsNames: Selector<string[]> = createSelector(
  getPlaylistTags,
  (playlistTags: ReturnType<typeof getPlaylistTags>) =>
    playlistTags.map(tag => tag.name),
)
/**
 * Gets native categories with their associated tags, sorted alphabetically.
 * @param state - The Redux state
 * @returns Array of native category objects with populated native_tags
 */
export const getNativeCategories: Selector<NativeCategory[]> = createSelector(
  (state: State) => state.entities.nativeCategories,
  (state: State) => state.entities.nativeTags,
  (
    nativeCategories: Record<string, NativeCategory>,
    nativeTags: Record<string, NativeTag>,
  ) => {
    const nativeCategoriesArray = Object.values(nativeCategories) as any
    return nativeCategoriesArray.sort(sortAlphabetically).map(category => ({
      ...category,
      native_tags: category.native_tags
        .map(tagId => nativeTags[tagId])
        .sort(sortAlphabetically),
    }))
  },
)
/**
 * Gets all native tags with their category information populated.
 * @param state - The Redux state
 * @returns Array of native tags with category objects attached
 */
export const getNativeTags: Selector<NativeTagWithCategory[]> = createSelector(
  (state: State) => state.entities.nativeCategories,
  (state: State) => state.entities.nativeTags,
  (
    nativeCategories: Record<string, NativeCategory>,
    nativeTags: Record<string, NativeTag>,
  ) => {
    const nativeTagsArray = Object.values(nativeTags) as any as NativeTag[]
    return nativeTagsArray.sort(sortAlphabetically).map(tag => ({
      ...tag,
      // nativeTags and nativeCategories are normally populated together by
      // normalizeNativeTags; if a category entry is missing (corrupt data),
      // fall back to a minimal category so the tag still surfaces rather than
      // being dropped from the UI.
      category: nativeCategories[tag.category_id] ?? {
        id: tag.category_id,
        name: '',
        native_tags: [],
      },
    }))
  },
)

/**
 * Gets native tags filtered by the provided IDs.
 * @param state - The Redux state
 * @param props - Object containing the tag IDs
 * @param props.ids - Array of tag IDs to filter by
 * @returns Array of native tags that match the provided IDs
 */
export const getNativeTagsById = createSelector(
  getNativeTags,
  (_state, props) => props.ids,
  (nativeTags: ReturnType<typeof getNativeTags>, ids: number[]) => {
    return nativeTags.filter(tag => ids.includes(tag.id))
  },
)

/**
 * Gets all native tags in a flattened array with category information.
 * @param state - The Redux state
 * @returns Flattened array of all native tags with their categories
 */
export const getFlattenedNativeTags = createSelector(
  [getNativeCategories],
  (nativeCategories: ReturnType<typeof getNativeCategories>) =>
    flatMap(nativeCategories, category =>
      category.native_tags.map(v => ({ ...v, category: category })),
    ),
)
